import { useEffect, useState } from "react";
import styled, { css } from "styled-components";

export type PaginationProps = {
  totalPages: number;
  page?: number;
  maxNeighbours: number;
  fixedEnds?: boolean;
  onChange: (page: number) => void;
};

const range = (start: number, end: number) =>
  Array.from({ length: end - start }, (v, k) => k + start);

const Pagination = (args: PaginationProps) => {
  const [active, setActive] = useState(args.page || 1);

  const pages = range(
    Math.max(1, active - args.maxNeighbours),
    Math.min(args.totalPages + 1, active + args.maxNeighbours + 1)
  );

  useEffect(() => {
    if (args.onChange) {
      args.onChange(active);
    }
  }, [active]);

  return (
    <PaginationElement>
      <Arrow left double onClick={() => setActive(1)} disabled={active == 1} />
      <Arrow
        left
        onClick={() => setActive(Math.max(1, active - 1))}
        disabled={active == 1}
      />
      {pages[0] != 1 && args.fixedEnds && (
        <Page onClick={() => setActive(1)}>1</Page>
      )}
      {pages[0] != 1 && pages[0] != 2 && (
        <Page
          onClick={() =>
            setActive(Math.max(1, active - args.maxNeighbours * 2))
          }
        >
          …
        </Page>
      )}
      {pages.map((page) => (
        <Page
          key={page}
          active={active == page}
          onClick={() => setActive(page)}
        >
          {page}
        </Page>
      ))}
      {pages[pages.length - 1] != args.totalPages &&
        pages[pages.length - 1] != args.totalPages - 1 && (
          <Page
            onClick={() =>
              setActive(
                Math.min(args.totalPages, active + args.maxNeighbours * 2)
              )
            }
          >
            …
          </Page>
        )}
      {pages[pages.length - 1] != args.totalPages && args.fixedEnds && (
        <Page onClick={() => setActive(args.totalPages)}>
          {args.totalPages}
        </Page>
      )}
      <Arrow
        onClick={() => setActive(Math.min(args.totalPages, active + 1))}
        disabled={active == args.totalPages}
      />
      <Arrow
        double
        onClick={() => setActive(args.totalPages)}
        disabled={active == args.totalPages}
      />
    </PaginationElement>
  );
};

const Arrow = styled.li<{
  left?: boolean;
  double?: boolean;
  disabled?: boolean;
}>`
  width: 24px;
  height: 24px;
  position: relative;
  cursor: pointer;
  &:hover::before,
  &:hover::after {
    border-color: var(--nf-primary);
  }
  &:before,
  &:after {
    content: "";
    box-sizing: border-box;
    width: 3px;
    height: 3px;
    border: solid var(--nf-secondary);
    border-width: 0 2px 2px 0;
    display: inline-block;
    padding: 3px;
    ${(props) => {
      if (props.left) {
        return css`
          transform: rotate(135deg);
          -webkit-transform: rotate(135deg);
        `;
      } else {
        return css`
          transform: rotate(-45deg);
          -webkit-transform: rotate(-45deg);
        `;
      }
    }}
    position: absolute;
    left: 50%;
    margin-left: -3px;
    top: 50%;
    margin-top: -5px;
  }
  &:after {
    content: none;
  }
  ${(props) =>
    props.double &&
    css`
      &:before {
        margin-left: -6px;
      }
      &:after {
        content: "";
        margin-left: 2px;
      }
    `}
  ${(props) =>
    props.disabled &&
    css`
      cursor: auto;
      &:hover::before,
      &:hover::after,
      &:after,
      &:before {
        border-color: var(--nf-gray-200);
      }
    `}
`;

const Page = styled.li<{ active?: boolean }>`
  height: 24px;
  width: 24px;
  text-align: center;
  vertical-align: center;
  font-size: 16px;
  margin: 0px 5px 0px 5px;
  color: var(--nf-gray-200);
  cursor: pointer;
  &:hover {
    color: var(--nf-secondary);
  }
  &:nth-child(3) {
    margin-left: 20px;
  }
  &:nth-last-child(3) {
    margin-right: 20px;
  }
  ${(props) =>
    props.active &&
    css`
      color: var(--nf-primary);
      &:hover {
        color: var(--nf-primary);
      }
    `}
`;

const PaginationElement = styled.ul`
  list-style-type: none;
  padding: 0;
  margin: 0;
  display: inline-flex;
`;

const defaultProps = {
  maxNeighbours: 2,
  fixedEnds: true,
};

Pagination.defaultProps = defaultProps;

export default Pagination;
